import {
  BMElevationType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemArrowLeft from "@traveloka/icon-kit-web/react/IcSystemArrowLeft";
import { memo, useEffect, useRef } from "react";
import {
  Animated,
  LayoutAnimation,
  Platform,
  Pressable,
  StyleSheet,
  UIManager,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";

type Props = {
  variant: "DEFAULT" | "INVERTED";
  onClickBack: () => void;
};

if (
  Platform.OS === "android" &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

function FlightPrebookHeader({ variant, onClickBack }: Props) {
  const insets = useSafeAreaInsets();
  const elevationOpacity = useRef(new Animated.Value(0)).current;

  const { BMComponents, BMElevation } = useBMTheme();
  const styles = createStyles(BMElevation);

  const { CorporateMobileFlightPrebook: cr } = useMobileContentResource();

  useEffect(() => {
    LayoutAnimation.configureNext({
      duration: 280,
      create: { type: "easeInEaseOut", property: "opacity" },
      update: { type: "easeInEaseOut", property: "opacity" },
      delete: { type: "easeInEaseOut", property: "opacity" },
    });
    Animated.timing(elevationOpacity, {
      toValue: variant === "DEFAULT" ? 1 : 0,
      duration: BMPrimitives.BMAnimation.duration.instant,
      useNativeDriver: true,
    }).start();
  }, [variant]);

  return (
    <BMView>
      <LinearGradient
        end={{ x: 1, y: 0 }}
        start={{ x: 0, y: 0 }}
        colors={["#0A9AF2", "#087DC4"]}
        style={styles.zIndex2}
      >
        <BMView
          row
          spacing="md"
          align="center"
          justify="center"
          style={[
            styles.container,
            {
              paddingTop: BMPrimitives.BMSize.md + insets.top,
              backgroundColor:
                variant === "DEFAULT"
                  ? BMComponents.BMStyleSurfaceBgcolor
                  : "transparent",
            },
          ]}
        >
          <Pressable onPress={onClickBack}>
            <BMView>
              <IcSystemArrowLeft
                width={24}
                height={24}
                color={
                  variant === "DEFAULT"
                    ? BMComponents.BMStyleIconInkPrimary
                    : BMComponents.BMStyleIconInkPrimaryInvert
                }
              />
            </BMView>
          </Pressable>
          <BMView justify="center" align={"center"} flex>
            <BMText
              variant="titleSmall"
              ink={
                variant === "DEFAULT"
                  ? "BMStyleTextInkPrimary"
                  : "BMStyleTextInkPrimaryInvert"
              }
            >
              {cr.title}
            </BMText>
          </BMView>
        </BMView>
      </LinearGradient>
      <Animated.View
        pointerEvents="none"
        style={[styles.fakeShadow, { opacity: elevationOpacity }]}
      />
    </BMView>
  );
}

export default memo(FlightPrebookHeader);

function createStyles(BMElevation: BMElevationType) {
  return StyleSheet.create({
    container: {
      padding: BMPrimitives.BMSize.md,
      paddingRight: BMPrimitives.BMSize.xl4,
    },
    zIndex2: {
      zIndex: 2,
    },
    fakeShadow: {
      zIndex: 1,
      backgroundColor: "white",
      ...StyleSheet.absoluteFillObject,
      ...BMElevation.elevation2,
    },
  });
}
